import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import * as d3 from 'd3-color';
import {
  FaceDirection,
  LOOP_COLOR_S11_LIST,
  RequestEvidences,
  S11MemoType,
  SendFaceDetection,
  SmsActionData,
  SmsOtpResponse,
  VerificationAction,
} from './pre-approve-models';
import { API_URL } from '@shared/service/api.constant';
import { ApiService } from '@core/http/api.service';
import { MemoDetail } from '../../memos/model/memo.model';
import { Negotiate } from '@store/approval-request/approval-request.actions';
import { MemoAttachment } from '@shared/models/memo.model';
import { HttpHeaders } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { VideoUploadService } from '@shared/service/video-upload.service';
import { VideoCompressionService } from '@shared/service/video-compression.service';

@Injectable({
  providedIn: 'root',
})
export class PreApproveService {
  stream: MediaStream | null = null;
  selectedTab: string | null | undefined = 'hirePurchaseAgreement';
  action: string | null = null;

  constructor(
    private http: ApiService,
    private translate: TranslateService,
    private videoUploadService: VideoUploadService,
    private videoCompressionService: VideoCompressionService,
  ) {}

  getWidgetColorsMap(members: any[]): string[][] {
    let colorPointer = 0;
    return members.map((member) => {
      if (Array.isArray(member)) {
        return member.map(() => {
          const color = LOOP_COLOR_S11_LIST[colorPointer];
          colorPointer += 1;
          colorPointer = colorPointer % LOOP_COLOR_S11_LIST.length;
          const hsl = d3.hsl(color);
          hsl.l += 0.25;
          return hsl.toString();
        });
      }
      return [];
    });
  }

  getDeviceType(): 'Mobile' | 'Tablet' | 'Desktop' {
    const ua = navigator.userAgent;

    if (
      /android|iphone|ipad|iPod|blackberry|windows phone/i.test(ua)
    ) {
      if (/tablet|ipad/i.test(ua)) return 'Tablet';
      return 'Mobile';
    }

    return 'Desktop';
  }

  getMemoDetailByType(
    id: number,
    memoType = S11MemoType.preApprove,
  ): Observable<MemoDetail> {
    return this.http.get(
      memoType === S11MemoType.preApprove
        ? API_URL.pre_approve_memo + id + '/'
        : memoType === S11MemoType.s11Contract
        ? API_URL.s11_contract + id + '/'
        : API_URL.other_reference_memo + id + '/',
    );
  }

  publishPreApproveMemoByType(
    id: number,
    memoType: string,
  ): Observable<{ qr_verification: string }> {
    return this.http.post(
      memoType === S11MemoType.preApprove
        ? API_URL.pre_approve_memo + id + '/publish/'
        : memoType === S11MemoType.s11Contract
        ? API_URL.s11_contract + id + '/publish/'
        : API_URL.other_reference_memo + id + '/publish/',
      null,
    );
  }

  getQrVerificationByType(
    id: number,
    memoType: string,
  ): Observable<{ qr_verification: string }> {
    return this.http.get(
      memoType === S11MemoType.preApprove
        ? API_URL.pre_approve_memo + id + '/qr-verification/'
        : memoType === S11MemoType.s11Contract
        ? API_URL.s11_contract + id + '/qr-verification/'
        : API_URL.other_reference_memo + id + '/qr-verification/',
    );
  }

  actionSmsOtpByType(
    id: number,
    typeSms: string,
    data: SmsActionData,
    memoType: string,
  ): Observable<SmsOtpResponse> {
    return this.http.post(
      memoType === S11MemoType.preApprove
        ? API_URL.pre_approve_memo + id + typeSms
        : memoType === S11MemoType.s11Contract
        ? API_URL.s11_contract + id + typeSms
        : API_URL.other_reference_memo + id + typeSms,
      data,
    );
  }

  approvePreApproveByType(id: number, data: any, memoType: string) {
    return this.http.post<MemoDetail>(
      memoType === S11MemoType.preApprove
        ? API_URL.pre_approve_memo + id + '/approve/'
        : memoType === S11MemoType.s11Contract
        ? API_URL.s11_contract + id + '/approve/'
        : API_URL.other_reference_memo + id + '/approve/',
      data,
    );
  }

  terminatePreApproveByType(id: number, data: any, memoType: string) {
    return this.http.post<MemoDetail>(
      memoType === S11MemoType.preApprove
        ? API_URL.pre_approve_memo + id + '/terminate/'
        : memoType === S11MemoType.s11Contract
        ? API_URL.s11_contract + id + '/terminate/'
        : API_URL.other_reference_memo + id + '/terminate/',
      data,
    );
  }

  uploadSelfieVideoByType(
    id: number,
    fd: FormData,
    memoType: string,
  ): Observable<any> {
    return this.http.post(
      memoType === S11MemoType.preApprove
        ? API_URL.pre_approve_memo + id + '/self-upload-selfie-video/'
        : memoType === S11MemoType.s11Contract
        ? API_URL.s11_contract + id + '/self-upload-selfie-video/'
        : API_URL.other_reference_memo +
          id +
          '/self-upload-selfie-video/',
      fd,
    );
  }

  /**
   * Upload selfie video with compression and retry for slow internet
   * @param id - Memo ID
   * @param file - Video file to upload
   * @param memoType - Type of memo
   * @returns Observable with upload result
   */
  uploadSelfieVideoOptimized(
    id: number,
    file: File,
    memoType: string,
  ): Observable<any> {
    const url =
      memoType === S11MemoType.preApprove
        ? API_URL.pre_approve_memo + id + '/self-upload-selfie-video/'
        : memoType === S11MemoType.s11Contract
        ? API_URL.s11_contract + id + '/self-upload-selfie-video/'
        : API_URL.other_reference_memo +
          id +
          '/self-upload-selfie-video/';

    // Check file size and determine upload strategy
    const fileSizeMB = file.size / (1024 * 1024);

    // For very large files, use chunked upload
    if (fileSizeMB > 20) {
      return this.videoUploadService.uploadVideoInChunks(url, file, {
        chunkSize: 2, // 2MB chunks for slow connections
        maxRetries: 5,
        retryDelay: 3000,
        compress: true,
        compressionOptions: {
          maxSizeMB: 5,
          maxWidthOrHeight: 720,
          quality: 0.7,
        },
      });
    }
    // For medium files, compress and upload
    else if (fileSizeMB > 5) {
      return this.videoUploadService.uploadVideoWithCompression(
        url,
        file,
        {
          compress: true,
          compressionOptions: {
            maxSizeMB: 3,
            maxWidthOrHeight: 720,
            quality: 0.8,
          },
          maxRetries: 3,
          retryDelay: 2000,
        },
      );
    }
    // For small files, upload directly with retry
    else {
      return this.videoUploadService.uploadVideoWithCompression(
        url,
        file,
        {
          compress: false, // No need to compress small files
          maxRetries: 3,
          retryDelay: 1000,
        },
      );
    }
  }

  /**
   * Get upload progress observable
   */
  getUploadProgress() {
    return this.videoUploadService.getUploadProgress();
  }

  /**
   * Check if browser supports video compression
   */
  isCompressionSupported(): boolean {
    return this.videoCompressionService.isCompressionSupported();
  }

  uploadEDocumentFiles(
    formData: FormData,
  ): Observable<{ attachments: MemoAttachment[] }> {
    return this.http.post(
      API_URL.e_document_upload_attachment,
      formData,
    );
  }

  getRequestEvidencesByType(
    id: number,
    memoType: string,
  ): Observable<RequestEvidences> {
    return this.http.post(
      memoType === S11MemoType.preApprove
        ? API_URL.pre_approve_memo + id + '/request-evidences/'
        : memoType === S11MemoType.s11Contract
        ? API_URL.s11_contract + id + '/request-evidences/'
        : API_URL.other_reference_memo + id + '/request-evidences/',
      null,
    );
  }

  selfAssign(id: number) {
    return this.http.post<MemoDetail>(
      API_URL.pre_approve_memo + id + '/assigned/',
      null,
    );
  }

  negotiate(id: number, data: Negotiate) {
    return this.http.post(
      API_URL.pre_approve_memo + id + '/negotiate/',
      data,
    );
  }

  uploadRequestAttachmentByType(
    id: number,
    data: any,
    header?: HttpHeaders,
    memoType = S11MemoType.preApprove,
  ): Observable<any> {
    const lang =
      this.translate.currentLang ||
      localStorage.getItem('lang') ||
      'th';
    let headers = header ?? new HttpHeaders();
    headers = headers
      .set('Content-Language', lang)
      .set('Accept-Language', lang);
    return this.http.post(
      memoType === S11MemoType.preApprove
        ? API_URL.pre_approve_memo +
            id +
            '/self-upload-request-attachments/'
        : memoType === S11MemoType.s11Contract
        ? API_URL.s11_contract +
          id +
          '/self-upload-request-attachments/'
        : API_URL.other_reference_memo +
          id +
          '/self-upload-request-attachments/',
      data,
      headers,
    );
  }

  getSelfRequestAttachmentsByType(
    id: number,
    data: { member_id: number | null },
    memoType: string,
  ): Observable<MemoAttachment[]> {
    return this.http.post(
      memoType === S11MemoType.preApprove
        ? API_URL.pre_approve_memo + id + '/self-request-attachments/'
        : memoType === S11MemoType.s11Contract
        ? API_URL.s11_contract + id + '/self-request-attachments/'
        : API_URL.other_reference_memo +
          id +
          '/self-request-attachments/',
      data,
    );
  }

  checkVerificationActionByType(
    id: number,
    memoType: string,
  ): Observable<VerificationAction> {
    return this.http.get(
      memoType === S11MemoType.preApprove
        ? API_URL.pre_approve_memo +
            id +
            '/check-verification-action/'
        : memoType === S11MemoType.s11Contract
        ? API_URL.s11_contract + id + '/check-verification-action/'
        : API_URL.other_reference_memo +
          id +
          '/check-verification-action/',
    );
  }

  getFaceDirection(data: {
    loa_member: number;
    memo_id: number;
    requester_id: number;
  }): Observable<FaceDirection> {
    return this.http.post<FaceDirection>(
      API_URL.face_direction + 'face-direction/',
      data,
    );
  }

  sendFaceDetection(
    data: SendFaceDetection,
    transactionsId: number,
  ): Observable<FaceDirection> {
    return this.http.patch<FaceDirection>(
      API_URL.face_direction + transactionsId + '/',
      data,
    );
  }

  submitS11Contract(
    memoId: number,
    data: any,
    type: string,
  ): Observable<any> {
    return this.http.post<any>(
      API_URL.s11_contract + memoId + type,
      data,
    );
  }

  submitOtherReferenceContract(
    memoId: number,
    data: any,
    type: string,
  ): Observable<any> {
    return this.http.post<any>(
      API_URL.other_reference_memo + memoId + type,
      data,
    );
  }

  checkSelfieVideoSigning(
    id: number,
    memoType: string,
  ): Observable<any> {
    return this.http.post(
      API_URL.memos +
        memoType +
        '/' +
        id +
        '/check-selfie-video-signing/',
      null,
    );
  }

  commanderRevise(
    id: number,
    data?: { reason: string },
  ): Observable<any> {
    return this.http.post(
      API_URL.s11_contract + id + '/commander-revise/',
      data,
    );
  }
}
